import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, CalendarDays, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, rupee, pnlClass, istDateTime, spreadText, STRATEGY_LABELS } from "@/lib/api";

const BOOKS = [["live", "Live"], ["dry", "Dry run"], ["paper", "Paper"], ["all", "All"]];
const BOOK_LABEL = { live: "LIVE", dry: "DRY", paper: "PAPER" };
const WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const ym = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const shift = (m, n) => { const [y, mo] = m.split("-").map(Number); return ym(new Date(y, mo - 1 + n, 1)); };
const label = (m) => new Date(`${m}-01T00:00:00`).toLocaleString("en-IN", { month: "long", year: "numeric" });

const cellStyle = (pnl, max) => {
  if (!pnl) return {};
  const a = 0.12 + 0.5 * Math.min(Math.abs(pnl) / (max || 1), 1);
  return { backgroundColor: pnl > 0 ? `rgba(16,185,129,${a})` : `rgba(239,68,68,${a})` };
};

export const PnlCalendar = ({ rev }) => {
  const [month, setMonth] = useState(null);
  const [kind, setKind] = useState("");
  const [book, setBook] = useState("live");
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);

  useEffect(() => {
    api.get("/execution/calendar", { params: { month: month || undefined, strategy: kind || undefined, book } }).then((r) => { setData(r.data); if (!month) setMonth(r.data.month); }).catch(() => {});
  }, [month, kind, book, rev]);

  if (!month) return null;
  const byDate = Object.fromEntries((data?.days || []).map((d) => [d.date, d]));
  const max = Math.max(1, ...(data?.days || []).map((d) => Math.abs(d.pnl)));
  const [y, mo] = month.split("-").map(Number);
  const lead = (new Date(y, mo - 1, 1).getDay() + 6) % 7;
  const n = new Date(y, mo, 0).getDate();
  const cells = [...Array(lead).fill(null), ...Array.from({ length: n }, (_, i) => `${month}-${String(i + 1).padStart(2, "0")}`)];
  const day = sel && (byDate[sel] || { date: sel, pnl: 0, trades: 0, wins: 0, list: [] });

  return (
    <div className="panel col-span-12 min-w-0 p-4" data-testid="panel-pnl-calendar">
      <div className="flex flex-wrap items-center gap-2">
        <div className="eyebrow flex items-center gap-2"><CalendarDays className="h-3.5 w-3.5" /> P&amp;L calendar</div>
        <div className="flex rounded-md border border-slate-700 p-0.5" data-testid="calendar-book-toggle">
          {BOOKS.map(([k, l]) => (
            <button key={k} type="button" data-testid={`calendar-book-${k}`} onClick={() => { setBook(k); setSel(null); }}
              className={`rounded px-2 py-0.5 text-[11px] font-medium transition-colors ${book === k ? "bg-indigo-500 text-white" : "text-slate-400 hover:text-slate-200"}`}>{l}</button>
          ))}
        </div>
        <div className="flex w-full items-center gap-1 sm:ml-auto sm:w-auto">
          <select data-testid="select-calendar-strategy" value={kind} onChange={(e) => { setKind(e.target.value); setSel(null); }}
            className="h-7 min-w-0 flex-1 rounded-md border border-input bg-slate-900 px-2 text-xs sm:flex-none">
            <option value="">All strategies</option>
            {Object.entries(STRATEGY_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <Button data-testid="button-calendar-prev" size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setMonth(shift(month, -1)); setSel(null); }}><ChevronLeft size={14} /></Button>
          <span className="w-28 text-center sm:w-32 text-sm font-semibold text-slate-200" data-testid="calendar-month-label">{label(month)}</span>
          <Button data-testid="button-calendar-next" size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setMonth(shift(month, 1)); setSel(null); }}><ChevronRight size={14} /></Button>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-5" data-testid="calendar-summary">
        <Sum t="Month P&L" v={<span className={pnlClass(data?.total)} data-testid="calendar-total">{rupee(data?.total ?? 0)}</span>} />
        <Sum t="Green / red days" v={<span data-testid="calendar-green-red"><span className="up">{data?.green ?? 0}</span> / <span className="down">{data?.red ?? 0}</span></span>} />
        <Sum t="Trades" v={<span data-testid="calendar-trades">{data?.trades ?? 0}</span>} />
        <Sum t="Best day" v={<span className="up">{data?.best != null ? rupee(data.best) : "—"}</span>} />
        <Sum t="Worst day" v={<span className="down">{data?.worst != null ? rupee(data.worst) : "—"}</span>} />
      </div>

      <div className="mt-3 grid grid-cols-7 gap-1">
        {WEEK.map((w) => <div key={w} className="eyebrow !text-[10px] text-center">{w}</div>)}
        {cells.map((c, i) => {
          if (!c) return <div key={`b${i}`} />;
          const d = byDate[c];
          const snap = data?.snapshots?.includes(c);
          return (
            <button key={c} type="button" data-testid={`calendar-day-${c}`} disabled={!d && !snap} onClick={() => setSel(sel === c ? null : c)}
              style={cellStyle(d?.pnl, max)}
              className={`flex min-h-[52px] flex-col items-start rounded-md border p-1 text-left transition-colors sm:min-h-[64px] sm:p-1.5 ${sel === c ? "border-indigo-400" : "border-slate-800"} ${d || snap ? "cursor-pointer hover:border-slate-500" : "cursor-default opacity-60"}`}>
              <span className="flex w-full items-center justify-between text-[10px] text-slate-400">{Number(c.slice(8))}{snap && <ImageIcon size={10} className="text-indigo-300" data-testid={`calendar-snap-icon-${c}`} />}</span>
              {d && <span className="num mt-auto text-[10px] font-semibold text-slate-100 sm:text-xs">{rupee(d.pnl)}</span>}
              {d && <span className="hidden text-[9px] text-slate-400 sm:block">{d.trades} tr{d.open ? " · open" : ""}</span>}
            </button>
          );
        })}
      </div>

      {day && (
        <div className="mt-3 border-t border-slate-800 pt-3" data-testid="calendar-day-detail">
          <div className="mb-2 flex items-center justify-between text-xs">
            <b className="text-slate-200">{sel} · {day.trades} trade{day.trades === 1 ? "" : "s"} · {day.wins} win{day.wins === 1 ? "" : "s"}</b>
            <span className={`num font-semibold ${pnlClass(day.pnl)}`}>{rupee(day.pnl)}</span>
          </div>
          <div className="space-y-1">
            {day.list.map((t) => (
              <div key={t.id} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 rounded border border-slate-800 px-2 py-1 text-[11px]" data-testid="calendar-trade-row">
                {book === "all" && <span className="rounded border border-slate-700 px-1 text-[9px] text-slate-400">{BOOK_LABEL[t.book]}</span>}
                <span className="font-semibold text-slate-200">{STRATEGY_LABELS[t.strategy_type] || t.strategy_type}</span>
                <span className="num text-slate-400">{spreadText(t)}</span>
                <span className="text-slate-500">{istDateTime(t.entry_ts)} → {t.exit_ts ? istDateTime(t.exit_ts) : t.status}</span>
                {t.exit_reason && <span className="text-slate-500">{t.exit_reason.replace(/_/g, " ")}</span>}
                <span className={`num ml-auto font-semibold ${pnlClass(t.pnl)}`}>{rupee(t.pnl)}</span>
              </div>
            ))}
          </div>
          <DaySnapshot day={sel} />
        </div>
      )}
      {data && !data.days.length && <p className="mt-2 text-[11px] text-slate-500" data-testid="calendar-empty">No {BOOKS.find((b) => b[0] === book)[1].toLowerCase()} trades this month.</p>}
    </div>
  );
};

const Sum = ({ t, v }) => (
  <div className="rounded-md border border-slate-800 px-2 py-1.5"><div className="eyebrow !text-[10px]">{t}</div><div className="num mt-0.5 text-sm font-semibold">{v}</div></div>
);

const inflight = {};
const freshSnap = (day) => {
  if (!inflight[day]) {
    inflight[day] = api.post("/reports/snapshot", null, { params: { date: day } }).then((r) => r.data);
    inflight[day].finally(() => setTimeout(() => delete inflight[day], 30000)).catch(() => {});
  }
  return inflight[day];
};

export const DaySnapshot = ({ day, fresh = false }) => {
  const [snap, setSnap] = useState(undefined);
  const [noData, setNoData] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setSnap(undefined);
    setNoData(false);
    const req = fresh ? freshSnap(day) : api.get("/reports/snapshot", { params: { date: day } }).then((r) => r.data.snapshot);
    req.then(setSnap).catch((e) => { if (fresh) { if (/no candles/i.test(errMsg(e))) setNoData(true); else toast.error(errMsg(e)); } setSnap(null); });
  }, [day, fresh]);
  const make = async () => {
    setBusy(true);
    try { setSnap((await api.post("/reports/snapshot", null, { params: { date: day } })).data); toast.success("Chart snapshot saved"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (snap === undefined) return <div className="mt-3 h-6 animate-pulse rounded bg-slate-800" />;
  if (noData) return <p className="mt-3 text-[11px] text-slate-500" data-testid="snapshot-no-data">No market data for {day} yet, so there's no chart to show. Try again after the market opens.</p>;
  if (!snap) return (
    <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-500" data-testid="calendar-no-snapshot">
      No chart snapshot saved for this day.
      <Button size="sm" variant="outline" disabled={busy} onClick={make} className="h-6 border-slate-700 text-[11px]" data-testid="button-calendar-make-snapshot">{busy ? "Saving…" : "Save snapshot now"}</Button>
    </div>
  );
  const src = `${process.env.REACT_APP_BACKEND_URL}${snap.url}`;
  return (
    <div className="mt-3" data-testid="calendar-snapshot">
      <div className="mb-1 flex items-center justify-between text-[11px] text-slate-500">
        <span>Chart snapshot · saved {istDateTime(snap.created_at)}</span>
        <a href={src} target="_blank" rel="noreferrer" className="text-indigo-300 hover:text-indigo-200" data-testid="link-calendar-snapshot">Open full size</a>
      </div>
      <a href={src} target="_blank" rel="noreferrer"><img src={src} alt={`NIFTY chart ${day}`} className="w-full max-w-4xl rounded-md border border-slate-800" data-testid="img-calendar-snapshot" /></a>
    </div>
  );
};
